import React, { ReactElement, useState } from 'react';
import {
  Box,
  ButtonBase,
  Drawer,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Category } from 'enum/categories';
import { CategoryIcon } from 'components/ui/CategoryIcon';
import {
  COLORS,
  FONT,
  MaterialSymbol,
  ScopeToggle,
  CategoryDropdown,
  SearchField,
  CategoryDropdownOption,
} from '../../shared';

export type EventScope = 'all' | 'following';

export interface EventsFilterBarProps {
  scope: EventScope;
  onScopeChange: (scope: EventScope) => void;

  allCount?: number;
  followingCount?: number;
  category: string | null;
  onCategoryChange: (category: string | null) => void;
  categoryOptions: CategoryDropdownOption[];
  search: string;
  onSearchChange: (value: string) => void;

  resultCount?: number;
}

export const EventsFilterBar = ({
  scope,
  onScopeChange,
  allCount,
  followingCount,
  category,
  onCategoryChange,
  categoryOptions,
  search,
  onSearchChange,
  resultCount,
}: EventsFilterBarProps): ReactElement => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [sheetOpen, setSheetOpen] = useState(false);

  const scopeOptions: [
    { value: EventScope; label: string; count?: number },
    { value: EventScope; label: string; count?: number },
  ] = [
    { value: 'all', label: 'All events', count: allCount },
    { value: 'following', label: 'Following', count: followingCount },
  ];

  const scopeToggle = (
    <ScopeToggle<EventScope>
      ariaLabel="Event scope"
      value={scope}
      options={scopeOptions}
      onChange={onScopeChange}
      fullWidth={!isDesktop}
    />
  );

  if (isDesktop) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          width: '100%',
          flexWrap: 'wrap',
        }}
      >
        {scopeToggle}
        <Box
          sx={{
            width: '1px',
            height: '24px',
            backgroundColor: COLORS.divider,
            flexShrink: 0,
          }}
        />
        <CategoryDropdown
          value={category}
          onChange={onCategoryChange}
          options={categoryOptions}
        />
        <SearchField
          value={search}
          onChange={onSearchChange}
          placeholder="Search events"
        />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            width: '100%',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>{scopeToggle}</Box>
          <ButtonBase
            onClick={() => setSheetOpen(true)}
            disableRipple
            aria-label="Open filters"
            sx={{
              flexShrink: 0,
              height: '48px',
              px: '20px',
              gap: '6px',
              borderRadius: '8px',
              backgroundColor: COLORS.white,
              border: '1px solid #E3EBF2',
              color: '#383838',
              fontFamily: FONT,
              fontWeight: 500,
              fontSize: '14px',
              whiteSpace: 'nowrap',
              '&:focus-visible': {
                outline: `2px solid ${COLORS.navy}`,
                outlineOffset: '-2px',
              },
            }}
          >
            <MaterialSymbol name="tune" size={18} color="#383838" />
            {category !== null ? 'Filter · 1' : 'Filter'}
          </ButtonBase>
        </Box>
        <SearchField
          value={search}
          onChange={onSearchChange}
          placeholder="Search events"
          fullWidth
        />
      </Box>

      <Drawer
        anchor="bottom"
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            boxShadow: '0 -4px 24px rgba(13,18,38,0.2)',
            maxHeight: '80vh',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            alignItems: 'center',
            pt: '12px',
            pb: '24px',
            px: '20px',
          }}
        >
          <Box
            sx={{
              width: '40px',
              height: '4px',
              borderRadius: '2px',
              backgroundColor: 'rgba(35,48,95,0.2)',
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 600,
                fontSize: '18px',
                color: COLORS.navy,
              }}
            >
              Filters
            </Typography>
            <ButtonBase
              onClick={() => onCategoryChange(null)}
              disableRipple
              sx={{
                fontFamily: FONT,
                fontWeight: 500,
                fontSize: '14px',
                color: COLORS.filterPlaceholder,
              }}
            >
              Clear all
            </ButtonBase>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 500,
                fontSize: '13px',
                color: COLORS.filterPlaceholder,
              }}
            >
              Category
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                width: '100%',
                overflowY: 'auto',
              }}
            >
              <CategorySheetItem
                label="All categories"
                iconCategory="All"
                selected={category === null}
                onClick={() => onCategoryChange(null)}
              />
              {categoryOptions.map(option => (
                <CategorySheetItem
                  key={option.value}
                  label={option.label}
                  iconCategory={option.value}
                  selected={option.value === category}
                  onClick={() => onCategoryChange(option.value)}
                />
              ))}
            </Box>
          </Box>

          <ButtonBase
            onClick={() => setSheetOpen(false)}
            disableRipple
            sx={{
              width: '100%',
              height: '50px',
              borderRadius: '8px',
              backgroundColor: COLORS.navy,
              color: COLORS.white,
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: '15px',
              '&:focus-visible': {
                outline: `2px solid ${COLORS.navy}`,
                outlineOffset: '2px',
              },
            }}
          >
            {resultCount === undefined
              ? 'Show events'
              : `Show ${resultCount} ${resultCount === 1 ? 'event' : 'events'}`}
          </ButtonBase>
        </Box>
      </Drawer>
    </>
  );
};

const CategorySheetItem = ({
  label,
  iconCategory,
  selected,
  onClick,
}: {
  label: string;
  iconCategory: Category;
  selected: boolean;
  onClick: () => void;
}): ReactElement => (
  <ButtonBase
    onClick={onClick}
    disableRipple
    role="option"
    aria-selected={selected}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      width: '100%',
      height: '48px',
      px: '10px',
      py: '8px',
      borderRadius: '8px',
      backgroundColor: selected ? 'rgba(35,48,95,0.08)' : 'transparent',
      '&:focus-visible': {
        outline: `2px solid ${COLORS.navy}`,
        outlineOffset: '-2px',
      },
    }}
  >
    <Box sx={{ flexShrink: 0 }}>
      <CategoryIcon category={iconCategory} size={32} selected={selected} />
    </Box>
    <Typography
      sx={{
        flex: '1 0 0',
        minWidth: 0,
        fontFamily: FONT,
        fontWeight: selected ? 600 : 500,
        fontSize: '14px',
        lineHeight: '20px',
        color: COLORS.navy,
        textAlign: 'left',
      }}
    >
      {label}
    </Typography>
    {selected && <MaterialSymbol name="check" size={18} color={COLORS.navy} />}
  </ButtonBase>
);
